'use client'

import type { RefObject } from 'react'
import { useEffect } from 'react'

import { getMainContentScrollContainer } from '@/shared/utils/getMainContentScrollContainer'
import { scrollMainContentToElement } from '@/shared/utils/scrollMainContentToElement'

import { SEARCH_MATCH_HIGHLIGHT_ATTRIBUTE, SEARCH_MATCH_HIGHLIGHT_RETRY_MS } from '../documentation-article.const'
import { clearSearchMatchHighlights } from '../utils/clearSearchMatchHighlights'
import { highlightSearchMatch } from '../utils/highlightSearchMatch'

function readHashId(): string {
  return window.location.hash.replace(/^#/, '')
}

function connectedHighlight(root: ParentNode): HTMLElement | null {
  const mark = root.querySelector<HTMLElement>(`[${SEARCH_MATCH_HIGHLIGHT_ATTRIBUTE}]`)
  return mark?.isConnected ? mark : null
}

export function useScrollToSearchMatch(
  containerRef: RefObject<HTMLElement | null>,
  currentPath: string,
  highlightQuery: string
) {
  useEffect(() => {
    const container = containerRef.current
    const hashId = readHashId()
    const trimmedHighlight = highlightQuery.trim()

    if (!trimmedHighlight && !hashId) {
      getMainContentScrollContainer()?.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    let cancelled = false
    let didScrollToHash = false
    const timers: ReturnType<typeof setTimeout>[] = []
    const lastDelay = SEARCH_MATCH_HIGHLIGHT_RETRY_MS[SEARCH_MATCH_HIGHLIGHT_RETRY_MS.length - 1]

    const attempt = (delay: number) => {
      if (cancelled) {
        return
      }

      const root = containerRef.current
      if (!root) {
        return
      }

      if (trimmedHighlight) {
        const existing = connectedHighlight(root)
        if (existing) {
          scrollMainContentToElement(existing)
          return
        }

        clearSearchMatchHighlights(root)
        const mark = highlightSearchMatch(root, trimmedHighlight)
        if (mark) {
          scrollMainContentToElement(mark)
          return
        }
      }

      const shouldScrollToHash = Boolean(hashId) && (!trimmedHighlight || delay === lastDelay)
      if (shouldScrollToHash && !didScrollToHash) {
        const heading = document.getElementById(hashId)
        if (heading) {
          didScrollToHash = true
          scrollMainContentToElement(heading)
        }
      }
    }

    for (const delay of SEARCH_MATCH_HIGHLIGHT_RETRY_MS) {
      timers.push(setTimeout(() => attempt(delay), delay))
    }

    return () => {
      cancelled = true
      timers.forEach((timer) => clearTimeout(timer))
      if (container) {
        clearSearchMatchHighlights(container)
      }
    }
  }, [containerRef, currentPath, highlightQuery])
}
